import { BehaviorSubject } from 'rxjs';
import { MentionableUser } from '../types';
import { fetchPortalUsers } from '../Api/Admin/PortalUsers/fetchPortalUsers';
import { fetchMyTeam } from '../Api/MyTeam/fetchMyTeam';
import { fetchMentionableUpstackers } from '../Api/User/fetchMentionableUpstackers';
import { getFeatureFlagSubject } from '../Api/useFeatureFlags';
import { getUser } from './User';
import { addError } from './Error';
import { MyTeam } from './GeneralStore';

const DOMAIN_KEY = 'comment';

const mentionableUpstackersSubject = new BehaviorSubject<MentionableUser[] | null>(null);
let inFlightUpstackers: Promise<MentionableUser[] | null> | null = null;

export const clearMentionableUpstackers = (): void => {
  inFlightUpstackers = null;
  mentionableUpstackersSubject.next(null);
};

const formatAdvisoryTeam = (myTeam: MyTeam | null): MentionableUser[] => {
  if (!myTeam) return [];
  return [myTeam.owner, myTeam.success_manager].map((member) => ({
    id: member.id,
    first_name: member.firstName,
    last_name: member.lastName,
    email: member.email
  }));
};

const fetchAccountUsers = (accountId: string): Promise<MentionableUser[]> =>
  fetchPortalUsers(accountId).then(({ data, error }) => {
    if (error) addError(DOMAIN_KEY, error.message);
    return data ?? [];
  });

const fetchAdvisoryTeam = (accountId: string): Promise<MentionableUser[]> =>
  fetchMyTeam(accountId).then(({ data, error }) => {
    if (error) addError(DOMAIN_KEY, error.message);
    return formatAdvisoryTeam(data);
  });

const getMentionableUpstackers = async (): Promise<MentionableUser[] | null> => {
  const cached = mentionableUpstackersSubject.getValue();
  if (cached) return cached;
  if (inFlightUpstackers) return inFlightUpstackers;

  inFlightUpstackers = fetchMentionableUpstackers().then(({ data, error }) => {
    if (data) mentionableUpstackersSubject.next(data);
    if (error) addError(DOMAIN_KEY, error.message);
    inFlightUpstackers = null;
    return data;
  });
  return inFlightUpstackers;
};

const fetchMentionableTeammates = async (accountId: string): Promise<MentionableUser[]> => {
  if (!getFeatureFlagSubject().getValue().mentionable_upstackers) return fetchAdvisoryTeam(accountId);

  const upstackers = await getMentionableUpstackers();
  return upstackers ?? fetchAdvisoryTeam(accountId);
};

const dedupeAndExcludeSelf = (users: MentionableUser[]): MentionableUser[] => {
  const currentId = getUser()?.id;
  const seen = new Set<string>();
  return users.filter((user) => {
    if (!user.id) return true;
    if (user.id === currentId) return false;
    if (seen.has(user.id)) return false;
    seen.add(user.id);
    return true;
  });
};

export const getMentionableUsers = async (accountId: string): Promise<MentionableUser[]> => {
  const [customers, teammates] = await Promise.all([
    fetchAccountUsers(accountId),
    fetchMentionableTeammates(accountId)
  ]);
  return dedupeAndExcludeSelf([...customers, ...teammates]);
};
